import { AbstractControl, FormGroup } from "@angular/forms";
import { HttpErrorResponse } from "@angular/common/http";

import { UNAVAILABLE_VALUE } from "../../core/constants/validation.constants";

/** Payment and checkout constants */
export const CONVENIENCE_FEE_RATE = 3; // percent of (subtotal + handling fee)
export const HANDLING_FEE = 5; // dollars
export const CHECK_MAILING_ADDRESS =
  'San Antonio Stock Show & Rodeo\n' +
  'Competitive Events Office\n' +
  'PO BOX 200230\n' +
  'San Antonio TX 78220';

/** Single user-facing label for any failure the API didn't explain itself.
 *  Change the wording here and every error toast in the app follows. */
export const GENERIC_ERROR_MESSAGE =
  'Something went wrong. Please try again or contact support if the issue persists.';

/** Avatar background colors for consistent hashing across the app */
export const AVATAR_COLORS = [
  'var(--p-app-avatar-indigo)',
  'var(--p-app-avatar-teal)',
  'var(--p-app-avatar-blue)',
  'var(--p-app-avatar-green)',
  'var(--p-app-avatar-pink)',
];

/**
 * Stable avatar colour for a name (or any id), hashed into AVATAR_COLORS.
 * The accumulator is an Int32Array so the hash keeps the 32-bit wraparound
 * the inline copies of this loop relied on — every name keeps the colour it
 * has always had.
 */
export function avatarColorFor(key: string): string {
  const identifier = key.length > 0 ? key : 'default';
  const hash = new Int32Array(1);

  for (const character of identifier) {
    hash[0] = Math.imul(hash[0], 31) + (character.codePointAt(0) ?? 0);
  }

  return AVATAR_COLORS[Math.abs(hash[0]) % AVATAR_COLORS.length];
}

export default class CommonUtils {

  /**
   * The `/v1/{show}/…` endpoints bury their payload as a JSON *string* under
   * `data[0][0].<Key>` — that string-encoding is why the raw response reads as
   * one backslash-escaped blob. Neither the nesting depth nor the encoding is in
   * the published contract (these endpoints are typed as a bare `BaseResponse`),
   * so probe the plausible depths and tolerate a value that arrives parsed.
   */
  static unwrapEnvelope<T = any>(response: any, key: string): T | null {
    // Build candidates list: check all data[i][0] elements, then data[0] and data
    const candidates = [response?.data?.[0], response?.data];
    if (Array.isArray(response?.data)) {
      for (let i = 0; i < response.data.length; i++) {
        if (response.data[i]?.[0]) {
          candidates.unshift(response.data[i][0]);
        }
      }
    }

    const cell = candidates.find((c) => c && typeof c === 'object' && key in c);
    const raw = cell?.[key];

    if (typeof raw !== 'string') {
      return (raw as T) ?? null;
    }

    try {
      return JSON.parse(raw) as T;
    } catch {
      console.error(`unwrapEnvelope: could not parse "${key}" payload`, raw);
      return null;
    }
  }

  /** Same, for keys whose payload is a list — an absent or malformed one is `[]`. */
  static unwrapEnvelopeList<T = any>(response: any, key: string): T[] {
    const value = CommonUtils.unwrapEnvelope<T[]>(response, key);
    return Array.isArray(value) ? value : [];
  }

  /** Extracts a human-readable message from an HTTP error, with a fallback. */
  static getErrorMessage(err: unknown, fallback = GENERIC_ERROR_MESSAGE): string {
    if (typeof err === 'string' && err.trim()) {
      return err;
    }
    if (err instanceof HttpErrorResponse) {
      const body = err.error;
      if (body && typeof body === 'object') {
        // BaseResponse error format (success: false, message: "...", errorDetails: "...")
        if (body.message && typeof body.message === 'string' && body.message.trim()) {
          return body.message;
        }
        if (body.Message && typeof body.Message === 'string' && body.Message.trim()) {
          return body.Message;
        }
        if (body.details && typeof body.details === 'string' && body.details.trim()) {
          return body.details;
        }
        // errorDetails is usually a code (like "InvalidUserName"), not a user message
        if (body.errorDetails && typeof body.errorDetails === 'string' && body.errorDetails.trim()) {
          return body.errorDetails;
        }
      }
      if (typeof body === 'string' && body.trim()) {
        return body;
      }
    }
    return fallback;
  }

static showErrorMessage(
    fg: FormGroup | AbstractControl | undefined,
    formControlName: string,
    errorType?: string
  ): boolean {
    // Guard: during initialization the form might not exist yet — a no-error
    // signal lets Angular render the form without a crash, then re-checks once
    // the FormGroup is ready and change detection runs again.
    if (!fg) {
      return false;
    }

    const controls = (fg instanceof AbstractControl ? (fg as FormGroup) : fg).controls;
    const fc = controls?.[formControlName];

    if (!fc) {
      return false;
    }

    if (!errorType) {
      return (fc.touched || fc.dirty) && fc.invalid;
    } else {
      return (fc.touched || fc.dirty) && fc.invalid && fc.getError(errorType);
    }
  }


  /**
   * Normalize API timestamps for proper timezone conversion.
   *
   * The API returns naive UTC timestamps without a 'Z' designator (e.g.,
   * "2026-08-24T13:36:25.4864294"). Without the 'Z', both `new Date()` and
   * Angular's `date` pipe interpret the string as *local* time rather than UTC,
   * causing the timestamp to render unchanged instead of being converted to the
   * viewer's timezone.
   *
   * This method appends 'Z' to mark the timestamp as UTC. Render it with the
   * `cstDate` pipe / `formatDateInCST`, not Angular's `date` pipe — that pipe
   * can't take an IANA zone and would show the viewer's local time.
   *
   * **Important:** Only timestamps with a time component (containing 'T') are
   * modified. Date-only values like "2026-08-24" (e.g., dates of birth) are left
   * unchanged to prevent off-by-one-day errors in western timezones.
   *
   * @param value - The date/timestamp to normalize (Date, ISO string, or null)
   * @returns ISO 8601 string with UTC designator ('Z'), or empty string if null
   */
  static getLocalDate(value: Date | string | null | undefined): string {
    if (!value) {
      return '';
    }
    if (value instanceof Date) {
      return value.toISOString();
    }
    const s = String(value);
    // Skip date-only values and timestamps that already have a timezone designator
    if (!s.includes('T') || /(?:Z|[+-]\d{2}:?\d{2})$/.test(s)) {
      return s;
    }
    return `${s}Z`;
  }

  /**
   * Format a date as MM/DD/YYYY in the *viewer's* timezone. Returns '' for
   * empty/invalid input.
   *
   * Prefer `formatDateOnly` (Central Time, for instants) or `formatDateOnlyUtc`
   * (for calendar days such as a date of birth) from `date-timezone.utils` —
   * this reads local getters, so the same value renders a different day
   * depending on where the viewer is. Safe only for a Date that already holds
   * wall-clock fields, e.g. one straight out of a datepicker.
   */
  static formatDate(value: Date | string | null | undefined): string {
    if (!value) {
      return '';
    }
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) {
      return '';
    }
    const mm = `${d.getMonth() + 1}`.padStart(2, '0');
    const dd = `${d.getDate()}`.padStart(2, '0');
    return `${mm}/${dd}/${d.getFullYear()}`;
  }

  /** Format a number as a USD currency string, e.g. 80 -> "$80.00". */
  static formatCurrency(amount: number): string {
    return `$${(amount || 0).toFixed(2)}`;
  }

  /** Parse a currency string back to a number, e.g. "$35.00" -> 35. */
  static parseCurrency(value: string | null | undefined): number {
    return Number((value ?? '').replace(/[^0-9.]/g, '')) || 0;
  }

  /** Two-letter uppercase initials from first/last name. */
  static initials(first?: string | null, last?: string | null): string {
    return `${(first ?? '').charAt(0)}${(last ?? '').charAt(0)}`.toUpperCase();
  }

  /** Trim a middle name for the payload, upper-casing any "n/a" spelling so
   *  the stored value is always "N/A". Empty becomes null. */
  static normalizeMiddleName(value?: string | null): string | null {
    const middleName = (value ?? '').trim();
    if (!middleName) {
      return null;
    }
    return /^n\s*\/\s*a$/i.test(middleName) ? UNAVAILABLE_VALUE : middleName;
  }

  /** Format full name as "Last First Middle" with proper spacing. */
  static formatFullName(
    first?: string | null,
    middle?: string | null,
    last?: string | null
  ): string {
    const parts: string[] = [];
    const lastName = (last ?? '').trim();
    const firstName = (first ?? '').trim();
    const middleName = (middle ?? '').trim();

    if (lastName) {
      parts.push(lastName);
    }
    if (firstName) {
      parts.push(firstName);
    }
    if (middleName) {
      parts.push(middleName);
    }

    return parts.join(' ');
  }

  /**
   * Split a joined "First Middle Last" name from the API into its parts.
   *
   * Several list endpoints send only a pre-joined `ExhibitorName` instead of the
   * three fields, so the parts have to be recovered before
   * {@link formatFullName} can re-order them. With three or more words the
   * middle ones are the middle name; with two the middle is empty. A trailing
   * word is always the last name, which is what the display leads with.
   */
  static splitFullName(name?: string | null): {
    firstName: string;
    middleName: string;
    lastName: string;
  } {
    const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);

    if (parts.length === 0) {
      return { firstName: '', middleName: '', lastName: '' };
    }
    if (parts.length === 1) {
      return { firstName: parts[0], middleName: '', lastName: '' };
    }

    return {
      firstName: parts[0],
      middleName: parts.slice(1, -1).join(' '),
      lastName: parts.at(-1)!
    };
  }

  /** Case-insensitive string equality; null/undefined-safe. */
  static equalsIgnoreCase(a: string | null | undefined, b: string | null | undefined): boolean {
    return (a ?? '').toLowerCase() === (b ?? '').toLowerCase();
  }

  /** True when the value is a UUID — used to tell IDs apart from display names on
   *  fields the API returns as either. */
  static isUuid(value: string | null | undefined): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value ?? '');
  }

  /** Parse an "M/D/YYYY" string to a local Date; null for empty/invalid. */
  static parseMMDDYYYY(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    const [m, d, y] = value.split('/').map(Number);
    if (!m || !d || !y) {
      return null;
    }
    return new Date(y, m - 1, d);
  }

  /** Plain count string for stat tiles (0, 3, 12). */
  static pad(n: number): string {
    return String(n);
  }

  /**
   * Get a consistent avatar background color for a given name.
   * Uses a simple hash function to map names to avatar colors.
   *
   * @param name The name to hash for color selection
   * @returns CSS variable for avatar background color
   */
  static getAvatarColor(name: string | null | undefined): string {
    if (!name) {
      return AVATAR_COLORS[0];
    }
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
  }
}
